import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';

import { FBoxContainer, FHButton, FHInputField } from '../../../base';
import { FORTA_BLUE, FORTA_ERROR } from '../../../../utils/colors';
import { usDollarFormatter } from '../../../../helpers';
import { getDueInDaysLabel, roundToCents } from './utils';
import ErrorIcon from '../../../../assets/images/error-alert.svg';

interface ISummaryBoxProps {
  totalDue: number;
  selectedPayee: number | null;
  dueInDays: number | null;
  clientName: string;
  totalNumClaimSelected: number;
  isInvoicePreviewCreated: boolean;
  discount: string | null;
  setDiscount: (discount: string | null) => void;
  onClick: () => void;
}

function SummaryBox({
  totalDue,
  selectedPayee,
  totalNumClaimSelected,
  isInvoicePreviewCreated,
  dueInDays,
  clientName,
  discount,
  setDiscount,
  onClick,
}: ISummaryBoxProps) {
  const enableGenerate = dueInDays && totalNumClaimSelected > 0 && selectedPayee;
  const [discountValueError, setDiscountValueError] = useState<boolean>(false);

  useEffect(() => {
    if (discount && roundToCents(Number(discount)) > roundToCents(totalDue)) {
      setDiscountValueError(true);
    } else {
      setDiscountValueError(false);
    }
  }, [discount, totalDue]);

  useEffect(() => {
    if (totalNumClaimSelected === 0) {
      setDiscount(null);
    }
  }, [totalNumClaimSelected]);

  const handleAmountUpdate = (value: string | null) => {
    if (!value && value !== '0') {
      setDiscount(null);
      return;
    }
    // Allow only 2 decimal places
    if (!/^\d+(\.\d{0,2})?$/.test(value)) {
      return;
    }
    setDiscount(value);
  };

  return (
    <FBoxContainer sx={{ ml: 3, minWidth: 360, maxHeight: 'auto', boxShadow: 'none' }}>
      <Box sx={{ textAlign: 'left' }}>
        <Typography fontWeight={700} fontSize={16} color={FORTA_BLUE.primary} mb={2}>
          Invoice details
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={700}>Client</Typography>
          <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
            {clientName}
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography fontWeight={700} sx={{ width: '90px', mt: 1, flexGrow: 1 }}>
            Due date
          </Typography>
          <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
            {dueInDays ? getDueInDaysLabel(dueInDays) : ''}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2, textAlign: 'left' }}>
        <Typography fontWeight={700}>Total no. of claims</Typography>
        <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
          {totalNumClaimSelected}
        </Typography>
      </Box>

      <Box sx={{ mb: 2, textAlign: 'left', opacity: totalNumClaimSelected < 1 ? 0.7 : 1 }}>
        <Typography fontWeight={700} mb={1}>
          Discount
        </Typography>
        <FHInputField
          placeholder="Enter amount"
          disabled={totalNumClaimSelected < 1}
          value={discount ?? ''}
          error={discountValueError}
          inputHeight={40}
          data-testid="discountAmount"
          sx={{ width: '180px', boxShadow: 'none' }}
          onChange={(e) => handleAmountUpdate(e.target.value ? e.target.value : null)}
        />
        {discountValueError && (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img src={ErrorIcon} width={16} height={16} alt={'discount-error'} />
            <Typography fontSize={14} color={FORTA_ERROR} ml={0.5}>
              Value should be less than amount billed
            </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography fontWeight={700}>Total due</Typography>
        <Typography fontWeight={700} fontSize={14} color={FORTA_BLUE.primary}>
          {usDollarFormatter.format(
            Math.max(0, roundToCents(totalDue - Number(discount ?? 0))),
          )}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'end' }}>
        <FHButton
          onClick={onClick}
          disabled={!enableGenerate || discountValueError}
          data-testid="createPreviewBtn"
        >
          {isInvoicePreviewCreated ? 'Go to preview' : 'Create preview'}
        </FHButton>
      </Box>
    </FBoxContainer>
  );
}

export default SummaryBox;
